import { FirebaseFormAssignmentService, AssignedForm as FirestoreAssignedForm } from './firebase/FirebaseFormAssignmentService';
import type { FormTemplate } from './firebase/FirebaseFormAssignmentService';
import { Unsubscribe } from 'firebase/firestore';

export type { FormTemplate };

export interface AssignedForm {
  id: string;
  clientId: string;
  providerId: string;
  formTemplateId: string;
  formTemplateName?: string;
  category?: string;
  assignedDate: string;
  assignedBy: string;
  completed: boolean;
  completedDate?: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
}

// Convert Firestore model (has extra fields) to legacy model
function toLocalForm(f: FirestoreAssignedForm): AssignedForm {
  return {
    id: f.id,
    clientId: f.clientId,
    providerId: f.providerId,
    formTemplateId: f.formTemplateId,
    formTemplateName: (f as any).formTemplateName,
    category: (f as any).category,
    assignedDate: f.assignedDate,
    assignedBy: f.assignedBy,
    completed: f.completed,
    completedDate: f.completedDate,
    dueDate: f.dueDate,
    priority: f.priority,
  };
}

class FormAssignmentService {
  private static instance: FormAssignmentService;

  public static getInstance(): FormAssignmentService {
    if (!FormAssignmentService.instance) {
      FormAssignmentService.instance = new FormAssignmentService();
    }
    return FormAssignmentService.instance;
  }

  // ─── Template Lookups (synchronous) ───

  public getAllFormTemplates(): FormTemplate[] {
    return FirebaseFormAssignmentService.getAllFormTemplates();
  }

  public getFormTemplatesByCategory(category: FormTemplate['category']): FormTemplate[] {
    return FirebaseFormAssignmentService.getFormTemplatesByCategory(category);
  }

  public getFormTemplateById(id: string): FormTemplate | undefined {
    return FirebaseFormAssignmentService.getFormTemplateById(id);
  }

  // ─── Assignment Methods (now async, backed by Firestore) ───

  public async getAssignmentsByClientId(clientId: string, providerId?: string): Promise<AssignedForm[]> {
    const assignments = await FirebaseFormAssignmentService.getAssignmentsByClientId(clientId, providerId);
    return assignments.map(toLocalForm);
  }

  public async assignFormToClient(
    clientId: string,
    formTemplateId: string,
    assignedBy: string,
    priority: AssignedForm['priority'] = 'medium',
    dueDate?: string
  ): Promise<void> {
    const template = this.getFormTemplateById(formTemplateId);
    await FirebaseFormAssignmentService.assignFormToClient({
      clientId,
      providerId: assignedBy,
      formTemplateId,
      formTemplateName: template?.name,
      category: template?.category,
      required: template?.required,
      priority,
      dueDate,
    });
  }

  public async removeFormAssignment(assignmentId: string): Promise<void> {
    await FirebaseFormAssignmentService.removeFormAssignment(assignmentId);
  }

  public async markFormAsCompleted(assignmentId: string): Promise<void> {
    await FirebaseFormAssignmentService.markFormAsCompleted(assignmentId);
  }

  public async getClientFormStatus(clientId: string, providerId?: string): Promise<{
    total: number;
    completed: number;
    pending: number;
    overdue: number;
    canBookAppointment: boolean;
  }> {
    return FirebaseFormAssignmentService.getClientFormStatus(clientId, providerId);
  }

  public async assignStandardFormsToNewClient(clientId: string, assignedBy: string): Promise<void> {
    await FirebaseFormAssignmentService.assignStandardForms(clientId, assignedBy);
  }

  public async canClientBookAppointment(clientId: string): Promise<boolean> {
    return FirebaseFormAssignmentService.canClientBookAppointment(clientId);
  }

  public async clearAllClientAssignments(clientId: string, providerId?: string): Promise<void> {
    if (!providerId) {
      console.warn('clearAllClientAssignments requires providerId');
      return;
    }
    await FirebaseFormAssignmentService.clearAllClientAssignments(clientId, providerId);
  }

  // ─── Real-time Subscription ───

  public subscribeToClientAssignments(
    clientId: string,
    callback: (assignments: AssignedForm[]) => void,
    providerId?: string
  ): Unsubscribe {
    return FirebaseFormAssignmentService.subscribeToClientAssignments(clientId, (assignments) => {
      callback(assignments.map(toLocalForm));
    }, providerId);
  }

  // Legacy methods — kept for backward compatibility but no longer used
  public subscribe(_listener: () => void): void {}
  public unsubscribe(_listener: () => void): void {}
  public getAllAssignments(): AssignedForm[] { return []; }
  public getCompletedFormDocuments(_clientId?: string): any[] { return []; }
  public canPatientBookIntake(_patientId: string): { canBook: boolean; missingForms: string[] } {
    return { canBook: true, missingForms: [] };
  }
  public assignSampleRequiredForms(_clientId: string, _assignedBy?: string): void {}
}

export default FormAssignmentService;
